import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Add24Regular, Dismiss24Regular, ChevronDown20Regular, ChevronUp20Regular } from '@fluentui/react-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { AppLoader } from '../appLoader/AppLoader';

export interface FAQItem {
  id: string;
  question: string;
  answer: string;
}

interface FAQPageProps {
  faqs?: FAQItem[];
  isLoading?: boolean;
}

const INITIAL_FAQS_DISPLAY = 3;

export const FAQPage: React.FC<FAQPageProps> = ({ faqs, isLoading = false }) => {
  const { t } = useTranslation();
  const [openValue, setOpenValue] = useState<string | undefined>(undefined);
  const [showAll, setShowAll] = useState(false);

  const displayFAQs = faqs && faqs.length > 0 ? faqs : [];
  const hasMoreFAQs = displayFAQs.length > INITIAL_FAQS_DISPLAY;
  const visibleFAQs = showAll ? displayFAQs : displayFAQs.slice(0, INITIAL_FAQS_DISPLAY);

  return (
    <div className="space-y-4">
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center justify-center gap-2">
            <AppLoader />
            <p className="-mt-10 text-neutral-20">{t('faq.loading')}</p>
          </div>
        </div>
      ) : displayFAQs.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-neutral-20">
            {t('faq.noResultsFound') || 'Aucun résultat trouvé.'}
          </div>
        </div>
      ) : (
        <>
          <Accordion
            type="single"
            collapsible
            className="w-full space-y-2"
            value={openValue}
            onValueChange={setOpenValue}
          >
            {visibleFAQs.map((faq) => {
              const isOpen = openValue === faq.id;
              return (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className="p-4 border rounded-2xl border-neutral-50 "
                >
                  <AccordionTrigger className="!py-0 text-left hover:no-underline">
                    <div className="flex items-center justify-between w-full ">
                      <span className="text-base font-bold text-neutral-10">
                        {faq.question}
                      </span>
                      {isOpen ? (
                        <Dismiss24Regular className="flex-shrink-0 w-6 h-6 transition-transform duration-200 text-neutral-10" />
                      ) : (
                        <Add24Regular className="flex-shrink-0 w-6 h-6 transition-transform duration-200 text-neutral-10" />
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent >
                    <p className="text-base text-neutral-20">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
          {/* View more/less link - only show if there are more than 3 FAQs */}
          {hasMoreFAQs && (
            <div className="flex justify-start pt-4">
              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className="flex items-center gap-2 underline text-primary hover:underline"
              >
                <span>
                  {showAll
                    ? t('faq.viewLess') || 'View less'
                    : t('faq.viewMore') || 'View more'}
                </span>
                {showAll ? (
                  <ChevronUp20Regular className="w-4 h-4" />
                ) : (
                  <ChevronDown20Regular className="w-4 h-4" />
                )}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FAQPage;
